/**
 * What a prescriber's signature covers on a plan that holds a microdose item (workbook v1.2, OPT-STEP-001: "the
 * signed plan holds the whole ladder ... The prescriber signs once"): the start dose, each step, HOLD, whether the
 * ceiling step is signed, the minimum weeks at each step and the sentence the patient will read.
 *
 * For the Review and sign drawer: <MicrodoseSignSummary planId={planId} /> (components/SignDrawer.tsx, above the
 * attestation). Renders nothing for a plan without a microdose item.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { IconSyringe } from './icons';
import type { DraftLadder } from './MicrodoseTypes';

export function MicrodoseSignSummary({ planId }: { planId: string }) {
  const query = useQuery({
    queryKey: ['microdose', 'plan-ladder', planId],
    queryFn: () => api.get<{ ladder: (Omit<DraftLadder, 'lockedReason'> & { signed: boolean }) | null }>(`/microdose/plans/${planId}/ladder`),
  });
  const ladder = query.data?.ladder;
  if (!ladder) return null;
  const steps = ladder.includeCeiling ? [...ladder.steps, ladder.ceiling] : ladder.steps;
  return (
    <div className="rounded-[12px] border border-forest/20 bg-mint/50 px-4 py-3.5" data-testid="sign-microdose-ladder">
      <p className="flex items-center gap-2 text-[14.5px] font-semibold text-navy">
        <IconSyringe size={17} className="text-forest" />
        {ladder.itemName}: the dose ladder you sign
      </p>
      <ol className="mt-2 flex flex-wrap gap-1.5" aria-label="Signed steps">
        {steps.map((s) => (
          <li key={s.step} className="rounded-md border border-forest/20 bg-white px-2.5 py-1 text-[13px] text-navy">
            <span className="mr-1 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-ink-muted">{s.label}</span>
            {s.text} mg
          </li>
        ))}
      </ol>
      <dl className="mt-2.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-[13px]">
        <dt className="text-ink-muted">Start dose</dt>
        <dd className="text-navy">{ladder.start.text} mg once a week</dd>
        <dt className="text-ink-muted">Ceiling step</dt>
        <dd className="text-navy">{ladder.includeCeiling ? `${ladder.ceiling.text} mg, signed` : 'Not signed'}</dd>
        <dt className="text-ink-muted">Minimum at each step</dt>
        <dd className="text-navy">{ladder.minWeeksAtStep} weeks</dd>
      </dl>
      <p className="mt-2.5 border-t border-forest/20 pt-2 text-[13px] leading-snug text-navy">
        <span className="text-ink-muted">The patient reads: </span>
        {ladder.patientSentence}
      </p>
    </div>
  );
}
